/**
 * Organization Registration Page.
 *
 * Public page for creating a new organization and owner account.
 * On success, the owner is logged in immediately and redirected
 * to the dashboard.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";

function RegisterOrgPage() {
  const navigate = useNavigate();
  const fetchUser = useAuthStore((s) => s.fetchUser);

  const [orgName, setOrgName] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setIsLoading(true);
    try {
      const resp = await apiClient.post("/api/v1/orgs/register", {
        org_name: orgName,
        owner_email: email,
        owner_password: password,
        owner_full_name: fullName,
      });

      // Store the token and fetch user profile.
      const { access_token } = resp.data as { access_token: string };
      localStorage.setItem("access_token", access_token);
      useAuthStore.setState({ token: access_token });
      await fetchUser();

      navigate("/", { replace: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(
        axiosErr.response?.data?.detail ||
          "Registration failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{
        background: `linear-gradient(135deg, var(--color-bg-gradient-from), var(--color-bg-gradient-via), var(--color-bg-gradient-to))`,
      }}
    >
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="mb-8 text-center">
          <img
            src="/logo.png"
            alt="TradeRoute"
            className="mx-auto mb-4 h-16 w-16 rounded-[var(--radius-xl)]"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            Create Your Account
          </h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Set up your organization and get started with TradeRoute.
          </p>
        </div>

        {/* Registration Form */}
        <div className="rounded-[var(--radius-xl)] border border-[var(--border-default)] bg-[var(--surface-base)] p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-[var(--radius-md)] bg-[var(--fill-danger)] p-3 text-sm text-[var(--text-danger)]">
                {error}
              </div>
            )}

            <Input
              label="Organization Name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              required
              placeholder="Acme Equipment LLC"
              autoFocus
            />

            <Input
              label="Your Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              placeholder="Jane Smith"
            />

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="jane@acme-equipment.com"
              autoComplete="email"
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="At least 6 characters"
              autoComplete="new-password"
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />

            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              Create Account
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-[var(--text-secondary)]">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-[var(--color-interactive-primary)] hover:text-[var(--color-interactive-primary)]"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterOrgPage;
